import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'

import { useMessage } from '@pepper/ui'
import { SEO_SERVICE_BASE_URL } from '@pepper/utils/src/api/config'
import axios from '@pepper/utils/src/function/axiosInstance'
import extract from '@pepper/utils/src/function/extract'

type ReturnType<T> = {
  data: T | undefined
  isLoading: boolean
  isFetching: boolean
  refetch: () => void
  error: Error | null
}

async function handleApiRequest<T>({
  reportId,
  companyId,
  documentId
}: {
  reportId: string
  companyId: string
  documentId: string
}): Promise<T> {
  const url = `${SEO_SERVICE_BASE_URL}/companies/${companyId}/content-gap/${reportId}/document/${documentId}`
  try {
    const response = await axios.get(url)
    return extract.data<T>(response).data
  } catch (error) {
    throw new Error(extract.error(error).error)
  }
}

const STALE_TIME_MS = 300000 // 5mins
export const useGetGapReportDocument = <T,>({
  companyId,
  reportId,
  documentId
}: {
  companyId: string
  reportId: string
  documentId: string
}): ReturnType<T> => {
  const { showError } = useMessage()

  const { isLoading, isFetching, isError, error, refetch, data } = useQuery<
    T,
    Error
  >({
    queryKey: ['content-gap-report', companyId, reportId, documentId],
    queryFn: () => handleApiRequest({ companyId, reportId, documentId }),
    enabled: !!companyId && !!reportId && !!documentId,
    retry: 3,
    staleTime: STALE_TIME_MS,
    cacheTime: STALE_TIME_MS
  })

  useEffect(() => {
    if (isError) {
      showError(error?.message)
    }
  }, [isError, error, showError])

  return {
    data,
    isLoading,
    isFetching,
    refetch,
    error
  }
}
